import { useState } from 'react'
import { licenceStatusLabel, daysUntilExpiry, summarise } from '../lib/licence'
import { initials } from './ui'

/**
 * The one rendering of a licence row, used by both the church's endorsement
 * dashboard (routes/admin/Endorsements.jsx) and the songwriter's licence
 * dashboard (routes/songwriter/Licences.jsx).
 *
 * Both parties are looking at the same agreement from opposite sides — if
 * each screen described it in its own words, "renews 12 Mar" on one side and
 * "up for renewal next month" on the other would drift into two different
 * stories about the same row. Routing every status word and every term
 * sentence through licence.js keeps that impossible: this component only
 * decides layout and emphasis, never wording.
 *
 * Deliberately does no data fetching — it takes rows the caller already
 * loaded (joined with the song title and the other party's name) and a
 * `perspective` so it knows which side of the row to show as "the other
 * party". Nothing here queries Supabase.
 */

const STATUS_TONE = {
  active: 'pf-pill-ok',
  proposed: 'pf-pill-review',
  wind_down: 'pf-pill-warn',
  expired: 'pf-pill-ready',
  declined: 'pf-pill-ready',
  revoked: 'pf-pill-ready',
}

// A countdown is only worth showing while the licence is actually heading
// toward a decision point — a declined or revoked row has nothing "upcoming"
// left to say.
const COUNTS_DOWN = ['active', 'wind_down', 'proposed']

const renewalWord = (days) => {
  if (days < 0) return 'overdue'
  if (days === 0) return 'today'
  return `${days} ${days === 1 ? 'day' : 'days'}`
}

/**
 * The decision log's vocabulary. Both portals read the same map, for the same
 * reason the status words live in licence.js: the church and the songwriter
 * must never be able to describe the same event differently.
 */
const EVENT_LABEL = {
  withdrawal_requested: 'Withdrawal requested',
  wind_down_started: 'Wind-down started',
  awaiting_reapproval: 'Re-approval needed',
  renewed: 'Renewed',
  restored: 'Put back on',
  revoked: 'Licence ended',
  expired: 'Licence ended',
}

const EVENT_WHY = {
  term_ended: 'the term ended',
  auto_renew: 'set to renew automatically',
  left_church: 'the songwriter left the church',
  left_worship_team: 'the songwriter came off the worship team',
  rejoined_church: 'the songwriter came back',
  rejoined_worship_team: 'the songwriter rejoined the team',
  withdrawn_by_songwriter: 'withdrawn by the songwriter',
  withdrawal_cancelled: 'the withdrawal was cancelled',
  reapproved_by_songwriter: 'approved for another term',
  declined_by_songwriter: 'not renewed',
  wind_down_complete: 'the wind-down finished',
}

const logDate = (iso) =>
  new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })

/**
 * onPush/pushingId/pcoConnected are church-side-only extras (Endorsements.jsx
 * passes them, Licences.jsx never does) — a songwriter isn't the one who
 * decides whether a song lands in the church's Planning Center, so the push
 * control only ever renders for perspective="church".
 *
 * onWithdraw/onReactivate are the mirror image: only the songwriter can take
 * their own song back, so those never render for perspective="church".
 *
 * `events` is a map of licence id to its history, fetched by the caller —
 * this component still queries nothing itself.
 */
export default function LicenceList({
  rows, perspective, emptyLabel = 'No endorsed songs yet.',
  onPush, pushingId, pcoConnected, rowErr,
  failures, onResolveByHand,
  onReapprove, decidingId,
  onWithdraw, onReactivate, events,
}) {
  const [openId, setOpenId] = useState(null)
  const [withdrawFor, setWithdrawFor] = useState(null)
  const [draft, setDraft] = useState({ reason: '', makePrivate: false })

  const startWithdraw = (id) => {
    setWithdrawFor(id)
    setDraft({ reason: '', makePrivate: false })
  }

  if (!rows || rows.length === 0) {
    return <p className="pf-empty">{emptyLabel}</p>
  }

  return (
    <div className="licence-list">
      {rows.map((row) => {
        const otherName = perspective === 'church' ? row.songwriter_name : row.church_name
        const days = daysUntilExpiry(row)
        const graceDays = row.wind_down_ends_at
          ? Math.ceil((new Date(row.wind_down_ends_at).getTime() - Date.now()) / 86400000)
          : null
        const showCountdown = days !== null && COUNTS_DOWN.includes(row.status)
        const urgent = showCountdown && days <= 14
        const isOpen = openId === row.id

        return (
          <div key={row.id} className="cosign" style={{
            flexWrap: 'wrap',
            alignItems: 'center',
            borderColor: urgent ? 'var(--danger)' : undefined,
          }}>
            <div className="ava" aria-hidden="true">{initials(otherName || row.song_title)}</div>

            <button
              type="button"
              className="who"
              style={{ textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={() => setOpenId(isOpen ? null : row.id)}
              aria-expanded={isOpen}
            >
              <div className="n">{row.song_title || 'Untitled'}</div>
              <div className="st">
                {otherName || '—'}
                {showCountdown && (
                  <span style={urgent ? { color: 'var(--danger)', fontWeight: 600 } : undefined}>
                    {' '}· {renewalWord(days)} {days >= 0 ? 'to renewal' : ''}
                  </span>
                )}
              </div>
            </button>

            <span className={`pf-pill ${STATUS_TONE[row.status] || 'pf-pill-ready'}`}>
              {licenceStatusLabel(row)}
            </span>

            {perspective === 'church' && onPush && row.status === 'active' && (() => {
              const failure = failures?.[row.song_id]
              // A song with an id but still an open failure is the partial
              // case — in the library, no chord chart. Saying "In Planning
              // Center" there would be true and useless, so the failure takes
              // precedence over the pill.
              if (row.pco_song_id && !failure) {
                return <span className="pf-pill pf-pill-ok" title="Already in Planning Center">In Planning Center</span>
              }

              // Three attempts is where automatic retrying stops earning its
              // keep. The button goes away rather than lying about what it
              // will do; adding it by hand is now the only path that works.
              const exhausted = failure && failure.attempts >= 3
              // 'auth' means the grant is broken and 'validation' means the
              // song is wrong. Neither is fixed by pressing the same button
              // again, so don't offer it.
              const retryPointless = failure
                && (failure.failure_class === 'auth' || failure.failure_class === 'validation')

              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  {!exhausted && !retryPointless && (
                    <button
                      className="pf-btn pf-btn-solid"
                      disabled={!pcoConnected || pushingId === row.id}
                      title={pcoConnected ? 'Push this song to Planning Center' : 'Connect Planning Center in Setup first'}
                      onClick={() => onPush(row)}
                    >
                      {pushingId === row.id
                        ? 'Pushing…'
                        : failure ? 'Retry push' : 'Push to Planning Center'}
                    </button>
                  )}

                  {failure && (
                    <>
                      <span
                        className={`pf-pill ${failure.failure_class === 'validation' || failure.failure_class === 'auth'
                          ? 'pf-pill-warn' : 'pf-pill-ready'}`}
                        title={failure.failure_reason || ''}
                      >
                        {exhausted
                          ? `Failed ${failure.attempts}×`
                          : failure.failure_class === 'auth' ? 'Reconnect needed'
                          : failure.failure_class === 'validation' ? 'Needs a fix'
                          : `Queued · attempt ${failure.attempts}`}
                      </span>
                      {onResolveByHand && (
                        <button className="pf-btn" onClick={() => onResolveByHand(failure)}>
                          Added by hand
                        </button>
                      )}
                    </>
                  )}
                </div>
              )
            })()}

            {failures?.[row.song_id]?.failure_reason && (
              <p className="small" style={{ flexBasis: '100%', margin: '6px 0 0', color: 'var(--muted)' }}>
                {failures[row.song_id].failure_reason}
              </p>
            )}

            {/* The engine parks a licence here when its term ends and the
                agreed terms require approving again. The notice email points
                the writer at this screen, so the answer has to live here. */}
            {perspective === 'writer' && onReapprove
              && row.status === 'wind_down' && row.wind_down_reason === 'awaiting_reapproval' && (
              <div style={{ flexBasis: '100%', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
                <p className="small" style={{ margin: '0 0 8px' }}>
                  {/* Counts to wind_down_ends_at, not expires_at — the term is
                      already past, so daysUntilExpiry would read "overdue". */}
                  The term has ended. {otherName || 'The church'} can keep using this
                  {graceDays !== null && graceDays >= 0 ? ` for ${renewalWord(graceDays)}` : ''} while you decide.
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="pf-btn pf-btn-solid" disabled={decidingId === row.id}
                          onClick={() => onReapprove(row, true)}>
                    {decidingId === row.id ? 'Working…' : 'Approve another term'}
                  </button>
                  <button className="pf-btn pf-btn-quiet" disabled={decidingId === row.id}
                          onClick={() => onReapprove(row, false)}>
                    Let it end
                  </button>
                </div>
              </div>
            )}

            {/* A withdrawal in progress, shown to whichever side is looking.
                The church is told plainly what was claimed and by when it
                ends; the writer gets the way back. */}
            {row.status === 'wind_down' && row.wind_down_reason === 'withdrawn_by_songwriter' && (
              <div style={{ flexBasis: '100%', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
                <p className="small" style={{ margin: '0 0 6px' }}>
                  <strong>Withdrawal requested.</strong>{' '}
                  {perspective === 'church'
                    ? `You can keep using this${graceDays !== null && graceDays >= 0 ? ` for ${renewalWord(graceDays)}` : ''}, then it goes inactive.`
                    : `${otherName || 'The church'} keeps using this${graceDays !== null && graceDays >= 0 ? ` for ${renewalWord(graceDays)}` : ''}, then it goes inactive.`}
                </p>
                {row.withdrawal_reason && (
                  <p className="small" style={{ margin: '0 0 8px', fontStyle: 'italic' }}>
                    “{row.withdrawal_reason}”
                  </p>
                )}
                {perspective === 'writer' && onReactivate && (
                  <button className="pf-btn pf-btn-quiet" disabled={decidingId === row.id}
                          onClick={() => onReactivate(row)}>
                    {decidingId === row.id ? 'Working…' : 'Cancel the withdrawal'}
                  </button>
                )}
              </div>
            )}

            {/* Withdrawn and now inactive. Nothing was deleted — the terms and
                the history are still on the row, and the writer can put it
                back on. */}
            {perspective === 'writer' && onReactivate
              && row.status === 'revoked' && row.wind_down_reason === 'withdrawn_by_songwriter' && (
              <div style={{ flexBasis: '100%', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
                <p className="small" style={{ margin: '0 0 8px' }}>
                  Inactive — {otherName || 'the church'} can no longer use this song. Nothing has
                  been deleted.
                </p>
                <button className="pf-btn pf-btn-solid" disabled={decidingId === row.id}
                        onClick={() => onReactivate(row)}>
                  {decidingId === row.id ? 'Working…' : 'Put it back on'}
                </button>
              </div>
            )}

            {/* Withdrawal is an escalation, not admin — so it asks for a
                reason the church will read, and never sits one tap from
                anything routine. */}
            {perspective === 'writer' && onWithdraw && row.status === 'active' && (
              withdrawFor === row.id ? (
                <div style={{ flexBasis: '100%', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
                  <p className="small" style={{ margin: '0 0 8px' }}>
                    This is for something serious — a copyright claim or a dispute. If you simply
                    don't want to renew, let the term run out instead.
                    {' '}{otherName || 'The church'} keeps using the song for 7 days, then it goes
                    inactive.
                  </p>
                  <textarea className="ta" value={draft.reason} rows={3}
                            placeholder="What has happened? The church is shown this."
                            onChange={(e) => setDraft({ ...draft, reason: e.target.value })} />
                  <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '8px 0' }}>
                    <input type="checkbox" checked={draft.makePrivate}
                           onChange={(e) => setDraft({ ...draft, makePrivate: e.target.checked })} />
                    <span>
                      Also make this song private — hides it from everyone on Beseter, not just
                      this church. Use this if the song itself is in dispute.
                    </span>
                  </label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="pf-btn pf-btn-solid"
                            disabled={!draft.reason.trim() || decidingId === row.id}
                            onClick={() => onWithdraw(row, draft.reason, draft.makePrivate)}>
                      {decidingId === row.id ? 'Working…' : 'Withdraw this licence'}
                    </button>
                    <button className="pf-btn pf-btn-quiet" onClick={() => setWithdrawFor(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button className="btn-plain" style={{ flexBasis: '100%', textAlign: 'left', paddingLeft: 0 }}
                        onClick={() => startWithdraw(row.id)}>
                  Withdraw this licence ›
                </button>
              )
            )}

            {isOpen && (
              <div style={{ flexBasis: '100%', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
                {summarise(row).map((line, i) => (
                  <p key={i} className="small" style={{ margin: '3px 0' }}>{line}</p>
                ))}

                {/* The decision log. Both sides read the same entries in the
                    same words — this is the record either party would point at
                    if the agreement is ever questioned. */}
                {events?.[row.id]?.length > 0 && (
                  <>
                    <div className="eyebrow" style={{ marginTop: 14, marginBottom: 6 }}>Decision log</div>
                    {events[row.id].map((ev) => (
                      <p key={ev.id} className="small" style={{ margin: '2px 0' }}>
                        {logDate(ev.occurred_at)} — {EVENT_LABEL[ev.event] || ev.event}
                        {ev.reason ? ` · ${EVENT_WHY[ev.reason] || ev.reason}` : ''}
                      </p>
                    ))}
                  </>
                )}
              </div>
            )}
            {rowErr?.[row.id] && (
              <p className="err" style={{ flexBasis: '100%', marginTop: 8 }}>{rowErr[row.id]}</p>
            )}
          </div>
        )
      })}
    </div>
  )
}
